/** Custom domain manager — Phase 3-pp-domain. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiPaths, api } from "@/lib/api";
import type { CustomDomain, CustomDomainVerifyResult } from "@/lib/types";

export function DomainsCard() {
  const qc = useQueryClient();
  const { t } = useTranslation();
  const [hostname, setHostname] = useState("");
  const [verifyMsg, setVerifyMsg] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ["org-domains"],
    queryFn: async () => {
      const res = await api.get<CustomDomain[]>(ApiPaths.orgDomains);
      return res.data;
    },
  });

  const add = useMutation({
    mutationFn: async () => {
      const res = await api.post<CustomDomain>(ApiPaths.orgDomains, {
        hostname: hostname.trim(),
      });
      return res.data;
    },
    onSuccess: () => {
      setHostname("");
      qc.invalidateQueries({ queryKey: ["org-domains"] });
    },
  });

  const verify = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post<CustomDomainVerifyResult>(
        ApiPaths.orgDomainVerify(id),
      );
      return res.data;
    },
    onSuccess: (result) => {
      setVerifyMsg(
        result.ok
          ? t("admin.domains.verifySuccess", { hostname: result.hostname })
          : t("admin.domains.verifyFail", { hostname: result.hostname }),
      );
      qc.invalidateQueries({ queryKey: ["org-domains"] });
    },
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(ApiPaths.orgDomain(id));
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["org-domains"] }),
  });

  return (
    <section className="card space-y-4" data-testid="domains-card">
      <header>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
          {t("admin.domains.cardTitle")}
        </h2>
        <p className="text-xs text-nb-text-3">{t("admin.domains.cardHint")}</p>
      </header>

      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          add.mutate();
        }}
      >
        <div className="flex-1 min-w-[200px]">
          <label className="label" htmlFor="dom-hostname">{t("admin.domains.hostname")}</label>
          <input
            id="dom-hostname"
            className="input"
            value={hostname}
            placeholder="inspect.acme.com"
            onChange={(e) => setHostname(e.target.value)}
          />
        </div>
        <button
          type="submit"
          className="btn-primary text-sm"
          disabled={!hostname.trim() || add.isPending}
        >
          {add.isPending ? t("admin.domains.adding") : t("admin.domains.addButton")}
        </button>
      </form>
      {add.isError && (
        <p className="text-xs text-cvp-danger" role="alert">
          {(() => {
            const err = add.error as unknown as {
              response?: { data?: { detail?: string } };
            };
            return err?.response?.data?.detail || t("admin.domains.addError");
          })()}
        </p>
      )}

      {verifyMsg && (
        <div
          className="rounded-md border border-nb-border bg-slate-900/40 px-3 py-2 text-xs text-nb-text"
          role="status"
        >
          {verifyMsg}
        </div>
      )}

      <ul className="space-y-2">
        {list.isLoading && <p className="text-nb-text-2 text-sm">{t("common.loading")}</p>}
        {list.data && list.data.length === 0 && (
          <p className="text-nb-text-2 text-sm">{t("admin.domains.noDomains")}</p>
        )}
        {(list.data ?? []).map((d) => (
          <li
            key={d.id}
            className="rounded-md border border-nb-border bg-slate-900/40 px-3 py-2 text-xs"
          >
            <div className="flex items-center justify-between gap-2">
              <code className="text-nb-text">{d.hostname}</code>
              <span
                className={`rounded px-2 py-0.5 ${
                  d.verified_at
                    ? "bg-emerald-500/15 text-emerald-300"
                    : "bg-cvp-warn/20 text-amber-200"
                }`}
              >
                {d.verified_at ? t("admin.domains.verified") : t("admin.domains.pending")}
              </span>
            </div>

            {!d.verified_at && (
              <div className="mt-2 space-y-1 text-nb-text">
                <p>{t("admin.domains.addRecord")}</p>
                <code className="block break-all rounded bg-slate-900 px-2 py-1 text-amber-200">
                  {d.verification_target}
                </code>
                <p className="text-nb-text-2">{t("admin.domains.withValue")}</p>
                <code className="block break-all rounded bg-slate-900 px-2 py-1 text-amber-200">
                  {d.verification_token}
                </code>
                <p className="text-nb-text-3">
                  {t("admin.domains.thenVerify", { hostname: d.hostname })}
                </p>
              </div>
            )}

            <div className="mt-2 flex flex-wrap gap-2">
              {!d.verified_at && (
                <button
                  type="button"
                  className="btn-primary text-xs"
                  onClick={() => verify.mutate(d.id)}
                  disabled={verify.isPending}
                >
                  {t("admin.domains.verifyButton")}
                </button>
              )}
              <button
                type="button"
                className="btn-danger text-xs"
                onClick={() => {
                  if (
                    window.confirm(
                      t("admin.domains.deleteConfirm", { hostname: d.hostname }),
                    )
                  ) {
                    remove.mutate(d.id);
                  }
                }}
                disabled={remove.isPending}
              >
                {t("common.delete")}
              </button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
